import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaDiscord,
  FaChevronDown,
  FaChevronUp,
  FaStickyNote,
  FaEnvelope,
  FaPhone,
  FaUserCheck,
} from "react-icons/fa";
import ActionIcon from "./ActionIcon";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";
import "../../../css/View/ActiveParticipantsList.css";
import { useToast } from "../../../Contexts/ToastContext";
import useApi from "../../CustomHooks/useApi";
import { participantsAPI } from "../../../api/apiClient";
import { useTripContext } from "../TripContext";
import exportParticipantsToExcel from "./ExportParticipantsToExcel";

const ParticipantsList = ({ participants = [], tripName, variantName }) => {
  const [expanded, setExpanded] = useState({});
  const [mode, setMode] = useState(null);
  const [statusMap, setStatusMap] = useState({});
  const { guid: tripGuid } = useParams();
  const navigate = useNavigate();
  const { showToast, clearToast } = useToast();
  const { callApi: saveAttendance, isLoading: isSavingAttendance } = useApi();
  const { updateParticipantsBulkInContext } = useTripContext();
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (mode === "attendance") {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handleBack = (e) => {
      if (mode === "attendance") {
        const confirmed = window.confirm(
          "Czy na pewno chcesz przejść wstecz? Wszystkie zmiany nie będą zapisane."
        );
        if (!confirmed) {
          e.preventDefault();
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handleBack);
    };
  }, [mode]);

  useEffect(() => {
    const statuses = {};
    participants.forEach((p) => {
      statuses[p.guid] = p.status;
    });
    setStatusMap(statuses);
  }, [participants]);

  const toggleExpanded = (guid) => {
    setExpanded((prev) => ({ ...prev, [guid]: !prev[guid] }));
  };

  const countSummary = participants.reduce(
    (acc, p) => {
      const status = mode === "attendance" ? statusMap[p.guid] : p.status;
      if (status === 2) acc.present++;
      else if (status === 3) acc.absent++;
      acc.total++;
      if (p.firstTime) acc.newbies++;
      return acc;
    },
    { present: 0, absent: 0, total: 0, newbies: 0 }
  );

  const toggleStatus = (guid) => {
    setStatusMap((prev) => {
      const current = prev[guid];
      let next;
      if (current === 1 || current === undefined) next = 2;
      else if (current === 2) next = 3;
      else next = 1;
      return { ...prev, [guid]: next };
    });
  };

  const handleModeToggle = (newMode) => {
    if (mode === newMode) {
      setMode(null);
      clearToast();
    } else {
      setMode(newMode);
    }
  };

  const handleSaveAttendance = async () => {
    const updates = participants.map((p) => ({
      guid: p.guid,
      status: statusMap[p.guid] ?? p.status,
    }));

    await saveAttendance(
      () => participantsAPI.setAttendance(updates, tripGuid),
      async () => {
        updateParticipantsBulkInContext(updates);
        clearToast();
        showToast("Obecność sprawdzona pomyślnie!", "success");
        setMode(null);
      }
    );
  };

  return (
    <div className={`participants-list-container ${mode ? "mode-active" : ""}`}>
      <h2 className="centered-heading">Lista osób</h2>

      <div className="action-buttons-bar">
        <ActionIcon
          icon={
            mode === "attendance" ? (
              <i className="fa-solid fa-floppy-disk" />
            ) : (
              <FaUserCheck />
            )
          }
          label={
            mode === "attendance"
              ? isSavingAttendance
                ? "Zapisywanie..."
                : "Zapisz"
              : "Sprawdź obecność"
          }
          active={mode === "attendance"}
          disabled={isSavingAttendance}
          onClick={() =>
            mode === "attendance"
              ? handleSaveAttendance()
              : handleModeToggle("attendance")
          }
        />
        <ActionIcon
          icon={<i className="fa-solid fa-file-excel" />}
          label="Eksport"
          onClick={() =>
            exportParticipantsToExcel(participants, tripName, variantName)
          }
        />
      </div>

      <div className="participant-filter-bar">
        <div className="filter-toggle-group">
          <div
            className={`filter-toggle ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
            title="Wszystkie osoby"
          >
            <i className="fa-solid fa-user" /> {countSummary.total}
          </div>
          <div
            className={`filter-toggle ${filter === "present" ? "active" : ""}`}
            onClick={() => setFilter("present")}
             title="Osoby obecne"
          >
            <i className="fa-solid fa-user-check text-blue" />{" "}
            {countSummary.present}
          </div>
          <div
            className={`filter-toggle ${filter === "absent" ? "active" : ""}`}
            onClick={() => setFilter("absent")}
             title="Osoby nieobecne"
          >
            <i className="fa-solid fa-user-xmark text-red" />{" "}
            {countSummary.absent}
          </div>
          <div
            className={`filter-toggle ${filter === "newbies" ? "active" : ""}`}
            onClick={() => setFilter("newbies")}
            title="Nowe osoby (pierwszy raz)"
          >
            <i className="fa-solid fa-hand-point-up text-green" />{" "}
            {countSummary.newbies}
          </div>
        </div>
      </div>

      <ul className="participants-list">
        {participants
          .filter((p) => {
            const currentStatus = mode === "attendance" ? statusMap[p.guid] : p.status;
            if (filter === "present") return currentStatus === 2;
            if (filter === "absent") return currentStatus === 3;
            if (filter === "newbies") return !!p.firstTime;
            return true;
          })
          .map((p) => {
            const currentStatus = mode === "attendance" ? statusMap[p.guid] : p.status;
            const style = getParticipantStatusStyle(currentStatus);

            return (
              <li key={p.guid} className={`participant-item ${style.statusClass}`}>
                <div
                  className="participant-row"
                  onClick={() => {
                    if (mode === "attendance") toggleStatus(p.guid);
                    else toggleExpanded(p.guid);
                  }}
                >
                  <div className={`participant-name ${style.statusClass}`}>
                    <i
                      className={`fa-solid ${style.icon} presence-icon ${style.statusClass}`}
                      title={style.label}
                    />
                    {p.preferredName}{" "}
                    {p.pronouns && <i>({p.pronouns})</i>}
                  </div>


                  <div className="participant-icons">
                    {p.coordinatorNotes && (
                      <FaStickyNote
                        className="note-icon"
                        title="Notatki koordynatora"
                      />
                    )}
                    {!mode &&
                      (expanded[p.guid] ? <FaChevronUp /> : <FaChevronDown />)}
                  </div>
                </div>

                {expanded[p.guid] && (
                  <div className="participant-details">
                    <div className="contact-info">
                      <div className="contact-line">
                        <span className="contact-icon">
                          <FaEnvelope />
                        </span>
                        <span>{p.email}</span>
                      </div>
                      <div className="contact-line">
                        <span className="contact-icon">
                          <FaPhone />
                        </span>
                        <span>{p.phone}</span>
                      </div>
                    </div>
                    {p.coordinatorNotes && (
                      <p className="coordinator-notes">
                        <em>{p.coordinatorNotes}</em>
                      </p>
                    )}
                    <button
                      className="profile-link"
                      onClick={() =>
                        navigate(
                          `/trips/${tripGuid}/variants/${p.variantGuid}/participants/${p.guid}`
                        )
                      }
                    >
                      Szczegóły
                    </button>
                  </div>
                )}
              </li>
            );
          })}
      </ul>

      {mode === "attendance" && (
        <div className="bottom-action-bar">
          <div className="attendance-legend" aria-label="Legenda obecności">
            <span><i className="fa-solid fa-user participantstatus-darkgray" aria-hidden="true" /> Nie sprawdzono</span>
            <span className="attendance-legend-separator" aria-hidden="true" />
            <span><i className="fa-solid fa-user-check participantstatus-blue" aria-hidden="true" /> Obecnx</span>
            <span className="attendance-legend-separator" aria-hidden="true" />
            <span><i className="fa-solid fa-user-xmark participantstatus-red" aria-hidden="true" /> Nieobecnx</span>
          </div>
          <div className="attendance-actions">
          <button className="cancel-button" onClick={() => setMode(null)}>
            Anuluj
          </button>
          <button
            className="save-button"
            onClick={handleSaveAttendance}
            disabled={isSavingAttendance}
          >
            {isSavingAttendance ? "Zapisywanie..." : "Zapisz obecności"}
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantsList;
